import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { Box } from '@wix/design-system';
import { Article, Ticket, TimelineItem } from '@wix/answers-api';
import { RelatedArticles } from '../../components/RelatedArticles';
import { BI } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { GetWixServerSidePropsFunction, HttpStatusCode, PageType } from '../../types';
import { TicketContent } from '../../components/TicketContent';
import { withServerSideProps } from '../../utils/with-server-side-props';
import { Context } from '../../context';
import Loader from '../../components/Loader';
import { loginWithAnswersSSO } from '../../utils/login';
import { Error } from '../../components/Error';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { mapPageTypeToSourceName } from '../../utils/bi';
import css from './index.module.scss';

const TicketPage: NextPage = () => {
  const {
    locale,
    query: { id },
  } = useRouter();
  const { sendBIEvent } = useBI();
  const { answersApi } = useContext(Context);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const { t } = useTranslation();
  const [errorStatusCode, setErrorStatusCode] = useState(0);
  const [ticket, setTicket] = useState<Ticket>();
  const [relatedArticles, setRelatedArticles] = useState<Article[]>();
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const { prePageUrl } = useHistoryContext();

  const onReplyAdded = (timelineItem: TimelineItem) => {
    setTimelineItems([...timelineItems, timelineItem]);
  };

  useEffect(() => {
    void (async () => {
      try {
        const { ticket: newTicket, timelineItems: newTimelineItems } =
          await answersApi.tickets.getUserTicketTimeline({
            ticketId: id as string,
          });
        const newRelatedArticles =
          await answersApi.articles.getHelpcenterTicketRelatedArticles({
            ticketId: id as string,
            itemsCount: 5,
          });

        if (newTicket.locale !== locale) {
          location.href = `/${newTicket.locale}/ticket/${newTicket.id}`;
          return;
        }
        setRelatedArticles(newRelatedArticles);
        setTicket(newTicket);
        setTimelineItems(newTimelineItems);
        setIsLoading(false);
      } catch (e: any) {
        setIsLoading(false);
        setIsError(true);
        const code = e?.response?.status || e?.code;
        setErrorStatusCode(code);
        if (code === HttpStatusCode.UNAUTHORIZED) {
          loginWithAnswersSSO(location.href, locale as string);
        }
      }
    })();
  }, [id, locale, answersApi, prePageUrl]);

  const onHomePageClick = async (): Promise<void> => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Ticket),
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.HOME_PAGE,
        clicked_text: '',
        clicked_url: `${location.origin}/${locale}`,
      })
    );
  };

  return !isError ? (
    <Box direction="vertical">
      {isLoading && <Loader />}
      <Breadcrumbs
        onHomePageClick={onHomePageClick}
        breadcrumbItems={[{ label: t('ticketpage.breadcrumbs.discussions') }]}
      />
      <Box
        direction="horizontal"
        width="100%"
        align="center"
        className={css.ticketWrapper}
      >
        {timelineItems && ticket && (
          <Box direction="vertical" className={css.leftSide}>
            <TicketContent
              ticket={ticket}
              timelineItems={timelineItems}
              onReplyAdded={onReplyAdded}
            />
          </Box>
        )}
        <Box direction="vertical" className={css.rightSide}>
          {relatedArticles && (
            <RelatedArticles
              relatedArticles={relatedArticles}
              pageType={PageType.Ticket}
              itemId={id as string}
              itemType={BI.ITEM_TYPES.TICKET}
            />
          )}
        </Box>
      </Box>
    </Box>
  ) : (
    <Error statusCode={errorStatusCode} />
  );
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ query }) => {
    const { id } = query;

    return {
      props: {
        pageType: PageType.Ticket,
        itemId: id,
        itemType: BI.ITEM_TYPES.TICKET,
      },
    };
  });

export default TicketPage;
